import * as THREE from 'three';
import React, {useEffect, useLayoutEffect, useMemo, useRef, useContext, useCallback, useState} from 'react'
import bgVert from './bg-vert.js'
import bgFrag from './bg-frag.js'
import tools from '../tools/tools.js';
import lerp from 'lerp'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass'
import { FilmPass } from 'three/examples/jsm/postprocessing/FilmPass'
import { WaterPass } from './postprocessing/WaterPass'

const waterPassFactor = 0.5

function mix(n1, n2, f){
	return n1 + (n2 - n1) * f
}
const FxContext = React.createContext({});
const SizeContext = React.createContext({});
function useFx(){
	return useContext(FxContext);
}
function useSize(){
	return useContext(SizeContext);
}
function MousePos(){
	const fx = useFx();
	const size = useSize()
	useEffect(() => {
		const content = fx.content;
		fx.mouseH = function(e){
			const rect = fx.dom.getBoundingClientRect();
			fx.mouse.clientX = e.clientX;
			fx.mouse.clientY = e.clientY;
			fx.mouse.x = (e.clientX - rect.left) / size.width * size.dpr;
			fx.mouse.y = 1 - (e.clientY - rect.top) /size.height * size.dpr;
			fx.move();
		}
		let whPos = window.scrollY
		function wheelH(e){
			if(whPos !== window.scrollY){
				whPos = window.scrollY
				fx.mouseH(e)
			}
		}
		function scrollH(e){
			if(whPos !== window.scrollY){
				whPos = window.scrollY
				fx.mouseH({
					clientX: fx.script.mouse.x,
					clientY: fx.script.mouse.y
				});
			}
		}
		content.addEventListener('pointermove', fx.mouseH, tools.passiveListener);
		document.body.addEventListener('wheel', wheelH, tools.passiveListener);
		document.addEventListener('scroll', scrollH);
		return () => {
			content.removeEventListener('pointermove', fx.mouseH);
			document.body.removeEventListener('wheel', wheelH);
			document.removeEventListener('scroll', scrollH);
		}
	}, [size, fx]);
	return null
}
function CanvasFx(props){
	const fx = useFx()
	const size = useSize()
	const [waterPass, setWaterPass] = useState(false)
	const {frameRun} = fx;
	useEffect(()=>{
		const passes = new Set()
		const fl = (v, def)=>{
			const res = parseFloat(v)
			return isNaN(res) ? def : res
		}
		let compo = false
		if(fx.waterPass){
			const wp = new WaterPass({factor: waterPassFactor})
			passes.add(wp)
			setWaterPass(wp)
		}
		if(fx.bloomPass){
			const bloomPassStrength = fl(fx.bloomPassStrength, 0.2)
			const bloomPassRadius = fl(fx.bloomPassRadius, 1)
			const bloomPassThreshold = fl(fx.bloomPassThreshold, 0)
			passes.add(new UnrealBloomPass(
				new THREE.Vector2(size.width, size.height),
				bloomPassStrength,
				bloomPassRadius, bloomPassThreshold
			))
		}
		if(fx.filmPass){
			passes.add(new FilmPass(0.25, 0.4, 1500, false))
		}
		if(passes.size>0){
			compo = new EffectComposer(fx.gl);
			compo.addPass(new RenderPass(fx.scene, fx.camera))
			passes.forEach((p)=>{
				compo.addPass(p)
			})
			compo.setSize(size.width, size.height)
			fx.setRender(()=>{compo.render()})
		}else{
			fx.setRender(null)
		}
		fx.setAutoPaused(!fx.noPaused);
		return ()=>{
			if(compo){
				compo.passes.forEach((pass)=>{
					if(pass.dispose){
						pass.dispose()
					}
				})
				if(compo.dispose){
					compo.dispose()
				}
			}
		}
	}, [fx,	size.width, size.height])
	useEffect(()=>{
		fx.move()
	}, [fx, fx.move])
	useEffect(()=>{
		if(waterPass){
			return frameRun((t, fadeOut)=>{
				waterPass.factor = waterPassFactor * (1 - fadeOut)
			})
		}
	},[frameRun, waterPass])
	return (
		<>
			<MousePos />
			{props.children}
		</>
	)
}
function loading({loader, url, fun}){
	if(url && loader){
		let stop = false;
		loader.load(url, function(lo){
			if(fun && !stop){
				fun(lo);
			}
		})
		return ()=> {stop = true}
	}else if(fun){
		fun(null);
	}
}
function imgLoading({url, width=false, height=false, textureSize=1.3, fun}){
	let loader
	if(url){
		/*
		When using TextureLoader, on Android we get error when displaying large
		textures (background jpegs 2560x3413, 2128x3200 or so) when loading an
		image directly from WordPress. The error does not occur if JetPack CDN is
		used or if low resolution images are used. The problem was found only on
		one device (weak Nokia), it was not tested on other devices.
		ImageBitmapLoader instead TextureLoader solve this issue.
		*/
		loader = new THREE.ImageBitmapLoader()
		const loaderOpts = {imageOrientation: 'flipY'}
		if(textureSize && width && height){
			const tWinth = screen.width * textureSize
			const tHeight = screen.height * textureSize
			if(width > tWinth && height > tHeight){
				const screenRatio = screen.width / screen.height
				const imgRatio = width / height
				if(screenRatio > imgRatio){
					loaderOpts.resizeWidth = tWinth
					loaderOpts.resizeHeight = loaderOpts.resizeWidth / imgRatio
				}else{
					loaderOpts.resizeHeight = tHeight
					loaderOpts.resizeWidth = loaderOpts.resizeHeight * imgRatio
				}
			}
		}
		loader.setOptions(loaderOpts);
	}
	return loading({loader, url, fun})
}
function textureLoading({url, fun}){
	let loader
	if(url){
		/*
		It is not optimized by screen size (see imgLoading).
		-------------------
		When using TextureLoader, on Android we get error when displaying large
		textures (background jpegs 2560x3413, 2128x3200 or so) when loading an
		image directly from WordPress. The error does not occur if JetPack CDN is
		used or if low resolution images are used. The problem was found only on
		one device (weak Nokia), it was not tested on other devices.
		ImageBitmapLoader instead TextureLoader solve this issue.
		*/
		loader = new THREE.TextureLoader()
	}
	return loading({loader, url, fun})
}
function color(col){
	if (typeof col === 'string' || col instanceof String){
		if(col.length === 9 && col.startsWith('#')){
			return new THREE.Color().setStyle(col.substring(0, 7))
		}else{
			return new THREE.Color().setStyle(col)
		}
	}else{
		return new THREE.Color(col)
	}
}
export {
	FxContext,
	SizeContext,
	mix,
	useFx, useSize, textureLoading, imgLoading, loading,
	CanvasFx,
	color
};
